import { type FocusEventHandler, useState } from 'react';

import Img from 'components/Img';
import { OptionalLink } from 'components/Link';
import Price, {
	BasePriceText,
	useBasePriceTextGetter,
	usePriceScreenReaderTextGetter,
} from 'components/Price';
import ProductTag, {
	useProductTagsScreenReaderTextGetter,
} from 'components/ProductTag';
import Text from 'components/Text';
import { usePriceContext } from 'contexts';
import { useInterval, useTrackEvent } from 'hooks';
import type { ProductCard } from 'models/productCard';
import type { ColorString } from 'types';
import { getProductImage } from 'utils/business-logic';
import { cn, cnm } from 'utils/classNames';
import { findNewIndex } from 'utils/collection';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

interface Props {
	/** Background color for any base price text */
	basePriceTextBackground?: ColorString;

	/** Extra container class names. */
	className?: string;

	/** If the animation container should cover (and stay within) available height. */
	coverHeight?: boolean;

	/** Play the animation? */
	isPlaying?: boolean;

	/** Handler for when focus leaves the container. */
	onContainerBlur?: FocusEventHandler<HTMLDivElement>;

	/** Handler for when focus enters the container. */
	onContainerFocus?: FocusEventHandler<HTMLDivElement>;

	/** Product link click handler. */
	onProductClick?: (product: ProductCard, index: number) => void;

	/** Products to animate. */
	products: ProductCard[];

	/** Show the product name below the image? */
	showTitle?: boolean;
}

/** An animated loop of linked product images. */
export default function ProductAnimation({
	basePriceTextBackground,
	className,
	coverHeight = false,
	isPlaying = true,
	onContainerBlur,
	onContainerFocus,
	onProductClick,
	products,
	showTitle = false,
}: Props) {
	const { t } = useI18n();
	const { sendProductClickEvent } = useTrackEvent();
	const { usePriceExcVat } = usePriceContext();

	const getPriceScreenReaderText = usePriceScreenReaderTextGetter();
	const getBasePriceText = useBasePriceTextGetter();
	const getTagsScreenReaderText = useProductTagsScreenReaderTextGetter();

	const [currentIndex, setCurrentIndex] = useState(0);
	const prevIndex = findNewIndex(products, currentIndex, 'prev');
	const nextIndex = findNewIndex(products, currentIndex, 'next');

	useInterval(
		() => {
			setCurrentIndex((i) => findNewIndex(products, i, 'next'));
		},
		isPlaying ? 5000 : null,
	);

	const imgClassName = cn(
		coverHeight && 'aspect-square h-auto w-auto max-w-full',
		coverHeight && !showTitle && 'max-h-full',
		// Approximate height for a single line of text, if the title
		// can be more than one line where this animation is used it
		// should take overflow into account.
		coverHeight && showTitle && 'max-h-[calc(100%-1.5rem)]',
	);

	return (
		<div
			className={cnm(
				'relative flex aspect-square justify-center',
				coverHeight && 'h-full',
				className,
			)}
			onFocus={(e) => {
				if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) {
					onContainerFocus?.(e);
				}
			}}
			onBlur={(e) => {
				if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) {
					onContainerBlur?.(e);
				}
			}}
		>
			{products.map((product, i) => {
				const basePriceText = getBasePriceText({
					basePrice: product.basePrice,
					displayedPriceType: product.displayPrice?.priceType,
					recentLowestPriceKey: 'product_animation_recent_lowest_price_text',
				});
				const tags = [
					product.productTags?.find(({ section }) => section === 'Flag'),
					product.productTags?.find(({ section }) => section === 'Product'),
					product.productTags?.find(({ section }) => section === 'Price'),
				].filter(is.truthy);

				return (
					<OptionalLink
						key={product.id}
						href={product.url}
						onLinkClick={() => {
							sendProductClickEvent(product.id);
							onProductClick?.(product, i);
						}}
						// Some screen readers (at least VoiceOver on macOS) will read the
						// link text based on visual order instead of source order, meaning
						// things can be read in an odd order. Label manually to ensure
						// correct order and commas for pauses.
						aria-label={[
							product.title,
							product.displayPrice &&
								getPriceScreenReaderText({ price: product.displayPrice }),
							basePriceText?.srLabel || basePriceText?.text,
							getTagsScreenReaderText(tags),
						]
							.filter(Boolean)
							.join(', ')}
						className={cn(
							// Parent components like BlockMedia might set pointer-events-none,
							// add back auto to make links clickable.
							'pointer-events-auto absolute top-0 inline-flex items-center justify-center transition-[visibility] duration-0 sm:justify-end',
							coverHeight && 'h-full',
							i !== currentIndex && 'invisible',
							i === prevIndex && 'delay-1000',
						)}
					>
						<div
							aria-hidden="true"
							className={cn(
								'flex origin-[center_left] flex-col items-center justify-center transition-[opacity,translate,scale] duration-700 sm:items-end',
								coverHeight && 'h-full',
								i !== currentIndex && 'opacity-0',
								i === currentIndex && 'translate-x-0 delay-400',
								i === nextIndex && 'translate-x-16',
								i === prevIndex && '-translate-x-10 scale-[0.6] duration-1000',
							)}
						>
							{[
								{ size: 393, cls: 'max-sm:hidden' },
								{ size: 192, cls: 'sm:hidden' },
							].map(({ cls, size }) => (
								<Img
									key={size}
									src={getProductImage(product)?.src}
									className={cn(imgClassName, cls)}
									width={size}
									height={size}
									jpgOptimized={false}
									alt=""
								/>
							))}
							<Text
								as="p"
								className={cn(
									'self-center text-center text-negative text-shadow-[#111]/20 text-shadow-[0_1px_2px]',
									!showTitle && 'sr-only',
								)}
							>
								{product.title}
							</Text>
							{tags.length > 0 && (
								<div className="absolute top-0 left-0 flex flex-col items-start gap-2">
									{tags.map((tag) => (
										<ProductTag
											key={tag.text}
											type={tag.type}
											text={
												usePriceExcVat && tag.textExcVat
													? tag.textExcVat
													: tag.text
											}
											size="md"
										/>
									))}
								</div>
							)}
						</div>
						{product.displayPrice && (
							<div
								aria-hidden="true"
								className={cn(
									'absolute transition-[opacity,translate,scale] duration-700',
									'max-sm:bottom-4 max-sm:-left-12',
									'sm:top-0 sm:right-0',
									i !== currentIndex && 'opacity-0',
									i === currentIndex && 'translate-y-0 delay-400',
									i === nextIndex && '-translate-y-4',
									i === prevIndex && 'translate-y-2 scale-75',
								)}
							>
								<div className="flex flex-col items-end">
									<Price
										price={product.displayPrice}
										regularPriceVariant="graphic"
									/>
									{usePriceExcVat && (
										<p
											style={
												basePriceTextBackground
													? { backgroundColor: basePriceTextBackground }
													: undefined
											}
											className={cn(
												'mt-1 text-xs text-negative',
												!basePriceTextBackground &&
													'shadow-base text-shadow-[#111] text-shadow-[0_0_4px]',
												basePriceTextBackground &&
													'inline rounded-2xs box-decoration-clone px-0.75 py-px shadow-base/50 text-shadow-[#111] text-shadow-[0_0_2px]',
											)}
										>
											{t('price_vat_excluded_text')}
										</p>
									)}
									<div
										className={cn(
											'absolute w-max max-w-36 text-xs text-balance text-negative',
											'max-sm:top-full max-sm:left-0 max-sm:mt-1',
											'sm:right-0 sm:bottom-full sm:mb-3 sm:text-right',
										)}
									>
										<BasePriceText
											basePrice={product.basePrice}
											displayedPriceType={product.displayPrice.priceType}
											recentLowestPriceKey="product_animation_recent_lowest_price_text"
											style={
												basePriceTextBackground
													? { backgroundColor: basePriceTextBackground }
													: undefined
											}
											className={cn(
												!basePriceTextBackground &&
													'shadow-base text-shadow-[#111] text-shadow-[0_0_4px]',
												basePriceTextBackground &&
													'inline rounded-2xs box-decoration-clone px-0.75 py-px shadow-base/50 text-shadow-[#111] text-shadow-[0_0_2px]',
											)}
										/>
									</div>
								</div>
							</div>
						)}
					</OptionalLink>
				);
			})}
		</div>
	);
}
ProductAnimation.displayName = 'ProductAnimation';
